import { Dialog as SheetPrimitive } from '@base-ui/react/dialog';
import { Trans } from '@lingui/react/macro';
import type { ReactNode } from 'react';

import XIcon from '@/assets/svg/bx-x.svg?react';
import { Button } from '@/components/buttons';
import { cn } from '@/lib/utils';

import SheetOverlay from './SheetOverlay';
import SheetPortal from './SheetPortal';

export type SheetSide = 'top' | 'right' | 'bottom' | 'left';

export type SheetContentProps = SheetPrimitive.Popup.Props & {
  side?: SheetSide;
  showCloseButton?: boolean;
  children?: ReactNode;
};

const SheetContent = ({
  className,
  children,
  side = 'right',
  showCloseButton = true,
  ...props
}: SheetContentProps) => {
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Popup
        data-slot="sheet-content"
        data-side={side}
        className={cn(
          'fixed z-1100 flex flex-col gap-4 bg-white bg-clip-padding text-sm shadow-lg',
          'transition duration-200 ease-in-out',

          'data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:border-t',
          'data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-3/4 data-[side=left]:border-r data-[side=left]:sm:max-w-sm',
          'data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-3/4 data-[side=right]:border-l data-[side=right]:sm:max-w-sm',
          'data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:border-b',

          'data-ending-style:opacity-0 data-starting-style:opacity-0',
          'data-[side=bottom]:data-ending-style:translate-y-10 data-[side=bottom]:data-starting-style:translate-y-10',
          'data-[side=left]:data-ending-style:-translate-x-10 data-[side=left]:data-starting-style:-translate-x-10',
          'data-[side=right]:data-ending-style:translate-x-10 data-[side=right]:data-starting-style:translate-x-10',
          'data-[side=top]:data-ending-style:-translate-y-10 data-[side=top]:data-starting-style:-translate-y-10',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close
            data-slot="sheet-close"
            render={
              <Button variant="ghost" className="absolute top-3 right-3" size="icon">
                <XIcon />
                <span className="sr-only">
                  <Trans>Close</Trans>
                </span>
              </Button>
            }
          />
        )}
      </SheetPrimitive.Popup>
    </SheetPortal>
  );
};

export default SheetContent;
